import { useQuery } from "@tanstack/react-query";

import { useAuthHeaders } from "../../auth/apiClient";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";

type EndpointState = "live" | "loading" | "synthetic";

type BrainStatePayload = {
  schema_version?: string;
  generated_at?: string;
  tenant?: { id?: number; label?: string; status?: string };
  runtime?: { commit?: { value?: string; source?: string } };
  counters?: Record<string, number>;
  side_effect_summary?: {
    mode?: string;
    decisions?: Array<{ label?: string; value?: string; ready?: boolean }>;
  };
  timers?: { live_state?: string; source?: string };
};

type MurrayTodayPayload = {
  generated_at?: string;
  todays_focus?: {
    id?: string;
    quote_id?: string;
    title?: string;
    action?: string;
    priority?: string;
    channel?: string;
    customer_name?: string;
    amount?: number;
    directive?: {
      action?: string;
      target_metric?: string;
      reason?: string;
    };
  };
  guardrails?: Record<string, unknown>;
};

type HandoffPayload = {
  handoff_summary?: {
    quote_id?: string;
    customer_name?: string;
    recommended_channel?: string;
    latest_objection_or_intent_subtype?: string;
    next_best_action?: {
      code?: string;
      label?: string;
      rationale?: string;
    };
    channel_rationale?: string;
    operator_brief?: string[];
    missing_context?: string[];
  };
};

type LabEndpoint<T> = {
  data: T | null;
  state: EndpointState;
  refetch: () => void;
};

const kenCase = {
  customer: "Ken Golden",
  quoteId: "265944",
  quoteValue: 3200,
  latestInput: "Another company came in lower. Can you match their price?",
  activeQuoteResolver: {
    outcome: "resolved",
    reason: "latest inbound SMS mapped to active quote 265944",
    reviewRequired: false,
  },
  intent: {
    category: "price_objection",
    subtype: "competitor_cheaper",
    confidence: 0.91,
    evidence: "lower, match their price",
    suggestedAction: "human_review_price_reply",
  },
  playbookDraft: {
    proofStatus: "approved",
    proofKey: "public_before_after_tile_grout_gallery",
    proofSourceLabel: "Sir Grout Jupiter before/after gallery",
    proofSourceUrl: "https://www.sirgroutjupiter.com/before-and-after/",
    socialProofKey: "public_google_reviews",
    socialProofSourceLabel: "Sir Grout Jupiter Google reviews",
    socialProofSourceUrl: "https://www.google.com/maps?cid=8214767487647707759",
    handoff: "quote_value_at_or_above_threshold",
    body:
      "Hi Ken, I understand the cost concern. The useful comparison is the full scope: prep, restoration method, materials, sealing when included, and the final finish you expect. You can review Sir Grout Jupiter's before/after gallery before deciding, so the comparison is based on visible examples instead of just price. I want Antonio to review this personally before we suggest the next step. Would you like us to compare the scope side by side so you can see what is included?",
    flags: [
      "human_validation_required",
      "never_discount_first",
    ],
  },
  handoff: {
    recommendedChannel: "call",
    nextBestAction: "call_price_objection",
    rationale:
      "High-value quote plus competitor price objection. Human should lead with scope, proof, and responsiveness before discussing changes.",
    brief: [
      "Acknowledge the price concern without discounting first.",
      "Compare included scope, prep, restoration method, materials, sealing when included, and visible proof.",
      "Only mention the approved ColorSeal 1-year guarantee when residential ColorSeal scope is confirmed.",
      "Offer a fast call with Antonio to protect trust and speed.",
    ],
  },
};

const syntheticBrainState: BrainStatePayload = {
  schema_version: "brain_state.v1",
  generated_at: "2026-06-26T18:00:00Z",
  tenant: { id: 1, label: "Sir Grout Jupiter", status: "lab" },
  runtime: { commit: { value: "main + lab demo", source: "synthetic" } },
  counters: {
    sms_pending_review: 1,
    murray_feedback_rows: 7,
    decision_events: 1,
    active_quote_resolutions: 1,
  },
  side_effect_summary: {
    mode: "human_review_required",
    decisions: [
      { label: "Customer sends", value: "blocked without owner approval", ready: true },
      { label: "Vonigo write", value: "not touched by lab", ready: true },
      { label: "Price proof", value: "before/after + Google reviews approved", ready: true },
      { label: "Quote threshold", value: "approved ($3000)", ready: true },
    ],
  },
  timers: { live_state: "not inspected in lab demo", source: "synthetic" },
};

const syntheticToday: MurrayTodayPayload = {
  generated_at: "2026-06-26T18:00:00Z",
  todays_focus: {
    id: "sms:SM-KEN-PRICE:next_action",
    quote_id: kenCase.quoteId,
    title: "Ken Golden raised a price objection",
    action: "Prepare a human-reviewed scope comparison reply.",
    priority: "High",
    channel: "sms",
    customer_name: kenCase.customer,
    amount: kenCase.quoteValue,
    directive: {
      action: "human_review_price_reply",
      target_metric: "price_objection_response_quality",
      reason: "competitor cheaper signal detected on an active quote",
    },
  },
  guardrails: {
    customer_facing_actions_require_human_approval: true,
    lab_mode: true,
  },
};

const syntheticHandoff: HandoffPayload = {
  handoff_summary: {
    quote_id: kenCase.quoteId,
    customer_name: kenCase.customer,
    recommended_channel: kenCase.handoff.recommendedChannel,
    latest_objection_or_intent_subtype: kenCase.intent.subtype,
    next_best_action: {
      code: kenCase.handoff.nextBestAction,
      label: "Antonio call before any price concession",
      rationale: kenCase.handoff.rationale,
    },
    channel_rationale: "Call is preferred because the objection is value-sensitive and quote value is above the approved threshold.",
    operator_brief: kenCase.handoff.brief,
    missing_context: [],
  },
};

async function fetchJson<T>(path: string, headers: Record<string, string>) {
  const res = await fetch(path, { headers, credentials: "include" });
  if (!res.ok) throw new Error(`Failed to fetch ${path}: ${res.status}`);
  return (await res.json()) as T;
}

function useLabEndpoint<T>(key: string, path: string): LabEndpoint<T> {
  const headers = useAuthHeaders();
  const enabled = Boolean(headers.Authorization);
  const query = useQuery({
    queryKey: ["murray-lab", key],
    enabled,
    retry: false,
    queryFn: () => fetchJson<T>(path, headers),
  });

  return {
    data: query.data ?? null,
    state: query.data ? "live" : query.isLoading && enabled ? "loading" : "synthetic",
    refetch: () => void query.refetch(),
  };
}

function sourceLabel(state: EndpointState) {
  if (state === "live") return "Live endpoint";
  if (state === "loading") return "Loading";
  return "Synthetic fallback";
}

function sourceVariant(state: EndpointState): "green" | "amber" | "slate" {
  if (state === "live") return "green";
  if (state === "loading") return "slate";
  return "amber";
}

function formatMoney(value: number | undefined) {
  const normalized = Number.isFinite(value) ? Number(value) : 0;
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(normalized);
}

function formatPercent(value: number) {
  return `${Math.round(value * 100)}%`;
}

function pickTodayFocus(today: MurrayTodayPayload) {
  return today.todays_focus ?? syntheticToday.todays_focus;
}

export function MurrayLabContent() {
  const brain = useLabEndpoint<BrainStatePayload>("brain-state", "/api/cockpit/brain-state");
  const today = useLabEndpoint<MurrayTodayPayload>("today", "/api/cockpit/murray-brain/today");
  const handoff = useLabEndpoint<HandoffPayload>(
    "handoff",
    `/api/cockpit/handoff-summary/${kenCase.quoteId}?recommendation_id=sms:SM-KEN-PRICE:next_action&resolver_outcome=resolved`,
  );

  const brainData = brain.data ?? syntheticBrainState;
  const todayData = today.data ?? syntheticToday;
  const handoffData = handoff.data ?? syntheticHandoff;
  const focus = pickTodayFocus(todayData);
  const handoffSummary = handoffData.handoff_summary ?? syntheticHandoff.handoff_summary;

  const refreshAll = () => {
    brain.refetch();
    today.refetch();
    handoff.refetch();
  };

  return (
    <section className="space-y-5">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="slate">Murray Lab</Badge>
            <Badge variant="green">Read only</Badge>
            <Badge variant="amber">No customer send</Badge>
          </div>
          <div>
            <h1 className="text-2xl font-semibold tracking-normal text-[#11133f] md:text-3xl">Murray Brain decision lab</h1>
            <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600">
              Concrete view of one customer signal moving through runtime state, triage, intent, sales playbook, and handoff.
            </p>
          </div>
        </div>
        <Button className="w-full sm:w-fit" variant="secondary" onClick={refreshAll}>
          Refresh lab readouts
        </Button>
      </div>

      <div className="grid gap-4 lg:grid-cols-[1.15fr_0.85fr]">
        <BrainStatePanel data={brainData} state={brain.state} />
        <TriagePanel data={todayData} state={today.state} focus={focus} />
      </div>

      <DecisionFlow />

      <div className="grid gap-4 xl:grid-cols-3">
        <IntentPanel />
        <PriceObjectionPanel />
        <HandoffPanel summary={handoffSummary} state={handoff.state} />
      </div>
    </section>
  );
}

function BrainStatePanel({ data, state }: { data: BrainStatePayload; state: EndpointState }) {
  const decisions = data.side_effect_summary?.decisions ?? syntheticBrainState.side_effect_summary?.decisions ?? [];
  const counters = Object.entries(data.counters ?? syntheticBrainState.counters ?? {}).slice(0, 4);
  const commit = data.runtime?.commit?.value ?? "unknown";

  return (
    <Card>
      <CardHeader className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <CardTitle>Brain State</CardTitle>
          <p className="mt-1 text-xs text-slate-500">Runtime, counters, side effects, and deployment context.</p>
        </div>
        <Badge variant={sourceVariant(state)}>{sourceLabel(state)}</Badge>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-3">
          <Metric label="Schema" value={data.schema_version || "brain_state.v1"} />
          <Metric label="Tenant" value={data.tenant?.label || `Tenant ${data.tenant?.id ?? 1}`} />
          <Metric label="Timer state" value={data.timers?.live_state || "lab"} />
        </div>
        <div className="grid gap-3 sm:grid-cols-4">
          {counters.map(([key, value]) => (
            <Metric key={key} label={key.replace(/_/g, " ")} value={String(value)} />
          ))}
        </div>
        <div className="rounded-md border border-slate-200 bg-slate-50 p-3">
          <div className="text-xs font-semibold uppercase tracking-normal text-slate-500">Runtime commit</div>
          <div className="mt-1 break-all font-mono text-xs text-slate-700">{commit}</div>
        </div>
        <div className="space-y-2">
          {decisions.map((decision) => (
            <div key={`${decision.label}-${decision.value}`} className="flex flex-col gap-2 rounded-md border border-slate-100 px-3 py-2 sm:flex-row sm:items-center sm:justify-between">
              <span className="text-sm font-medium text-slate-800">{decision.label}</span>
              <Badge variant={decision.ready ? "green" : "amber"}>{decision.value}</Badge>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function TriagePanel({
  data,
  state,
  focus,
}: {
  data: MurrayTodayPayload;
  state: EndpointState;
  focus: NonNullable<MurrayTodayPayload["todays_focus"]> | undefined;
}) {
  return (
    <Card>
      <CardHeader className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <CardTitle>Triage</CardTitle>
          <p className="mt-1 text-xs text-slate-500">Active input selected for owner attention.</p>
        </div>
        <Badge variant={sourceVariant(state)}>{sourceLabel(state)}</Badge>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="rounded-md border border-blue-100 bg-blue-50/70 p-4">
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="default">{focus?.priority || "High"}</Badge>
            <Badge variant="slate">{focus?.channel || "sms"}</Badge>
            <Badge variant="green">Quote #{focus?.quote_id || kenCase.quoteId}</Badge>
          </div>
          <h2 className="mt-3 text-lg font-semibold text-[#11133f]">{focus?.title || "Ken Golden raised a price objection"}</h2>
          <p className="mt-2 text-sm leading-6 text-slate-700">{focus?.action || focus?.directive?.action || syntheticToday.todays_focus?.action}</p>
        </div>
        <div className="space-y-3 text-sm">
          <KeyValue label="Customer input" value={kenCase.latestInput} />
          <KeyValue label="Resolver outcome" value={`${kenCase.activeQuoteResolver.outcome}: ${kenCase.activeQuoteResolver.reason}`} />
          <KeyValue label="Quote value" value={formatMoney(focus?.amount || kenCase.quoteValue)} />
          <KeyValue label="Generated at" value={data.generated_at || "2026-06-26T18:00:00Z"} />
        </div>
      </CardContent>
    </Card>
  );
}

function DecisionFlow() {
  const steps = [
    { label: "Input", value: "Inbound SMS price concern" },
    { label: "Resolver", value: "Active quote 265944" },
    { label: "Intent v2", value: "competitor_cheaper" },
    { label: "Playbook", value: "proposal_only draft" },
    { label: "Handoff", value: "Antonio call" },
  ];

  return (
    <Card>
      <CardContent className="grid gap-3 p-4 md:grid-cols-5">
        {steps.map((step, index) => (
          <div key={step.label} className="relative rounded-md border border-slate-200 bg-white p-3">
            <div className="text-xs font-semibold uppercase tracking-normal text-slate-500">{step.label}</div>
            <div className="mt-2 min-h-10 text-sm font-medium leading-5 text-[#11133f]">{step.value}</div>
            {index < steps.length - 1 ? (
              <div className="absolute -right-2 top-1/2 hidden h-4 w-4 -translate-y-1/2 rotate-45 border-r border-t border-slate-200 bg-white md:block" aria-hidden="true" />
            ) : null}
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

function IntentPanel() {
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-3">
          <CardTitle>Intent v2</CardTitle>
          <Badge variant="green">{formatPercent(kenCase.intent.confidence)}</Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-3">
          <KeyValue label="Category" value={kenCase.intent.category} />
          <KeyValue label="Subtype" value={kenCase.intent.subtype} />
          <KeyValue label="Evidence" value={kenCase.intent.evidence} />
          <KeyValue label="Suggested action" value={kenCase.intent.suggestedAction} />
        </div>
        <div className="rounded-md border border-emerald-100 bg-emerald-50 p-3 text-sm leading-6 text-emerald-950">
          Decision: price should be handled as a value and scope conversation, not as an automatic discount.
        </div>
      </CardContent>
    </Card>
  );
}

function PriceObjectionPanel() {
  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle>Price Objection L1</CardTitle>
          <Badge variant={kenCase.playbookDraft.proofStatus === "approved" ? "green" : "amber"}>{kenCase.playbookDraft.proofStatus}</Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-sm leading-6 text-slate-700">
          {kenCase.playbookDraft.body}
        </div>
        <div className="space-y-3">
          <KeyValue label="Side effect class" value="proposal_only" />
          <KeyValue label="Proof key" value={kenCase.playbookDraft.proofKey} />
          <KeyValue label="Proof source" value={kenCase.playbookDraft.proofSourceLabel} />
          <KeyValue label="Source URL" value={kenCase.playbookDraft.proofSourceUrl} />
          <KeyValue label="Social proof key" value={kenCase.playbookDraft.socialProofKey} />
          <KeyValue label="Social proof source" value={kenCase.playbookDraft.socialProofSourceLabel} />
          <KeyValue label="Social proof URL" value={kenCase.playbookDraft.socialProofSourceUrl} />
          <KeyValue label="Human handoff" value={kenCase.playbookDraft.handoff} />
        </div>
        <div className="flex flex-wrap gap-2">
          {kenCase.playbookDraft.flags.map((flag) => (
            <Badge key={flag} variant={flag.includes("pending") ? "amber" : "slate"}>
              {flag}
            </Badge>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function HandoffPanel({
  summary,
  state,
}: {
  summary: NonNullable<HandoffPayload["handoff_summary"]> | undefined;
  state: EndpointState;
}) {
  const brief = summary?.operator_brief?.length ? summary.operator_brief : kenCase.handoff.brief;
  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle>Handoff Summary</CardTitle>
          <Badge variant={sourceVariant(state)}>{sourceLabel(state)}</Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-1">
          <Metric label="Recommended channel" value={summary?.recommended_channel || kenCase.handoff.recommendedChannel} />
          <Metric label="Next action" value={summary?.next_best_action?.code || kenCase.handoff.nextBestAction} />
        </div>
        <p className="text-sm leading-6 text-slate-700">
          {summary?.next_best_action?.rationale || summary?.channel_rationale || kenCase.handoff.rationale}
        </p>
        <div className="space-y-2">
          {brief.map((item) => (
            <div key={item} className="flex gap-2 rounded-md border border-slate-100 px-3 py-2 text-sm text-slate-700">
              <span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-[#1587c8]" aria-hidden="true" />
              <span>{item}</span>
            </div>
          ))}
        </div>
        {summary?.missing_context?.length ? (
          <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
            Missing context: {summary.missing_context.join(", ")}
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border border-slate-200 bg-white p-3">
      <div className="text-xs font-semibold uppercase tracking-normal text-slate-500">{label}</div>
      <div className="mt-1 min-h-6 break-words text-sm font-semibold text-[#11133f]">{value}</div>
    </div>
  );
}

function KeyValue({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-xs font-semibold uppercase tracking-normal text-slate-500">{label}</div>
      <div className="mt-1 rounded-md border border-slate-100 bg-white px-3 py-2 text-sm leading-5 text-slate-700">{value}</div>
    </div>
  );
}
